import { useDialogState, useLoadingState } from 'hooks'
import { VehicleNeedsToBeMovedToStagingPhotoProps } from './index'
import { useState } from 'react'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import {
  NeedsClusterRemovedDetailsDto,
  NeedsClusterRemovedPulledClusterPhotoDto,
  NeedsToBeMovedToStagingDetailsDto,
  NeedsToBeMovedToStagingWarrantyCertPhotoDto,
  VehicleDto
} from 'dtos'
import { ArrowRight, ArrowLeft } from '@mui/icons-material'
import { ExplanationAccordion, GreenText } from 'components'
import PulledClusterPhotoDialog from '../clusterRemoved/PulledClusterPhotoDialog'
import NeedsToBeMovedToStagingStepper from './NeedsToBeMovedToStagingStepper'
import WarrantyCertPhotoDialog from './WarrantyCertPhotoDialog'
import WarrantyCertPhotoCard from './WarrantyCertPhotoCard'

interface Step2WarrantyCertPhotoProps extends VehicleNeedsToBeMovedToStagingPhotoProps {
  onBack: () => void
  onNext: () => void
  needsToBeMovedToStagingDetails: NeedsToBeMovedToStagingDetailsDto
  vehicleGuid?: string
}

export default function Step2PulledClusterPhoto({
  onBack,
  onNext,
  needsToBeMovedToStagingDetails,
  vehicleGuid,
  warrantyCertPhoto,
  setWarrantyCertPhoto
}: Step2WarrantyCertPhotoProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingWarrantyCertPhoto: false,
    isDeletingWarrantyCertPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    warrantyCertPhotoDialogOpen: false
  })

  const hasWarrantyCertPhoto = Boolean(warrantyCertPhoto.warrantyCertPhotoFileName)

  return (
    <>
      <WarrantyCertPhotoDialog
        initialValues={warrantyCertPhoto}
        isLoading={loadingState.isDeletingWarrantyCertPhoto || loadingState.isUpdatingWarrantyCertPhoto}
        onClose={() => {
          setDialogState('warrantyCertPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingWarrantyCertPhoto', true)
          controllers
            .deleteNeedsToBeMovedToStagingWarrantyCertPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Warranty Cert Photo Deleted Successfully', { variant: 'success' })
              setWarrantyCertPhoto(response.value)
              setDialogState('warrantyCertPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingWarrantyCertPhoto', false)
            })
        }}
        onSave={(values: NeedsToBeMovedToStagingWarrantyCertPhotoDto) => {
          setLoadingState('isUpdatingWarrantyCertPhoto', true)
          controllers
            .updateNeedsToBeMovedToStagingWarrantyCertPhotoInformation(vehicleGuid!, values)
            .then(response => {
              console.log('API Response:', response)
              if (response?.value) {
                setWarrantyCertPhoto(response.value)
                enqueueSnackbar('Warranty Cert Photo Updated Successfully', { variant: 'success' })
              } else {
                console.error('Unexpected API Response:', response)
              }
              setDialogState('warrantyCertPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingWarrantyCertPhoto', false)
            })
        }}
        open={dialogState.warrantyCertPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <NeedsToBeMovedToStagingStepper activeStep={1} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <WarrantyCertPhotoCard
                complete={Boolean(warrantyCertPhoto.warrantyCertPhotoFileName)}
                fileName={warrantyCertPhoto.warrantyCertPhotoFileName}
                onClick={() => {
                  setDialogState('warrantyCertPhotoDialogOpen', true)
                }}
                type='warranty-cert'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} marginTop={2}>
              <Button
                color='primary'
                endIcon={<ArrowRight />}
                fullWidth
                onClick={onNext}
                type='submit'
                variant='contained'
                disabled={!hasWarrantyCertPhoto}
              >
                NEXT
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button color='primary' startIcon={<ArrowLeft />} fullWidth onClick={onBack} variant='outlined'>
                PREVIOUS
              </Button>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Vehicle Steps:**

Displays the current step of the vehicle in the Needs To Be
Moved To Staging Process.

**Warrant Certificate In Glove Box:**

Displays the Warranty Certificate In Glove Box image uploaded
by the user.

If no image has been uploaded, a “No Image” icon will be
displayed instead.

Clicking the Warranty Certificate In Glove Box will open the
Capture Warranty Certificate In Glove Box Photo Dialog.

**NEXT Button:**

Clicking this button will:

- Save the uploaded photo to the database.

- Advance the user to the next step in the process.

This button remains disabled until a photo has been uploaded.

**PREVIOUS Button:**

Clicking this button will:

- Discard any uploaded photo that has not been saved.

- Return the user to the previous step in the process.

          `}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
